import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle, Search, UserPlus, X } from 'lucide-react'
import { tr } from 'i18n'
import LocalizedFileInput from 'components/Erp/LocalizedFileInput'
import { checkBaseOperatorInn } from 'service/AdminPanelService'
import { getErpErrorMessage } from 'service/ErpService'
import styles from './BaseOperatorForm.module.scss'
import management from '../Management.module.scss'

const blankUser = () => ({ password: '', surname: '', first_name: '', last_name: '', number_phone: '', email: '' })

export default function BaseOperatorForm({ locationKey, locationLabel, modalPayload = {}, createOperator,
  loadOperators = async () => {}, onOperatorSaved = async () => {}, closeModal = () => {}, notifySuccess = () => {}, notifyError = () => {}, uploadOperatorDocuments }) {
  useTranslation()
  const [inn, setInn] = useState('')
  const [lookup, setLookup] = useState(null)
  const [checking, setChecking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [user, setUser] = useState(blankUser)
  const [files, setFiles] = useState([])
  const requestVersion = useRef(0)
  const saving = useRef(false)
  const locationId = modalPayload[locationKey]
  useEffect(() => () => { requestVersion.current += 1 }, [])

  const changeInn = value => {
    requestVersion.current += 1
    setInn(value.replace(/\D/g, '').slice(0, 14))
    setLookup(null)
    setChecking(false)
    setUser(blankUser())
    setError('')
  }
  const search = async () => {
    if (!/^[0-9]{14}$/.test(inn) || checking || busy) return
    const version = ++requestVersion.current
    setChecking(true)
    setLookup(null)
    setError('')
    try {
      const { data } = await checkBaseOperatorInn(inn, { [locationKey]: locationId })
      if (version !== requestVersion.current) return
      if (!['found', 'available', 'blocked'].includes(data.status) || (data.status === 'found' && !data.user?.id)) throw new Error('Invalid lookup response')
      setLookup({ ...data, inn })
    } catch (e) {
      if (version === requestVersion.current) setError(getErpErrorMessage(e, tr('Не удалось проверить ИНН. Повторите проверку.')))
    } finally {
      if (version === requestVersion.current) setChecking(false)
    }
  }
  const ready = lookup?.inn === inn && ['found', 'available'].includes(lookup?.status)
  const isNew = ready && lookup.status === 'available'
  const save = async event => {
    event.preventDefault()
    if (!ready || saving.current) return
    saving.current = true
    setBusy(true)
    setError('')
    const payload = { [locationKey]: locationId }
    if (isNew) payload.new_user = { ...user, username: inn }
    else payload.existing_user = lookup.user.id
    try {
      const { data } = await createOperator(payload)
      if (files.length && data?.id) {
        try { await uploadOperatorDocuments(data.id, files) }
        catch (e) { notifyError(e, tr('Оператор создан, но не удалось загрузить документы')) }
      }
      // The assignment is already saved; a refresh failure must not invite a duplicate submission.
      try { await onOperatorSaved(lookup?.previous_locations || []); await loadOperators(locationId) }
      catch (e) { notifyError(e, tr('Оператор создан, но не удалось обновить список. Обновите страницу.')) }
      notifySuccess(tr('Оператор назначен на объект.'))
      closeModal()
    } catch (e) {
      setError(getErpErrorMessage(e, tr('Не удалось создать оператора')))
    } finally {
      saving.current = false
      setBusy(false)
    }
  }
  const field = (key, label, options = {}) => (
    <label className={styles.field}>{tr(label)}
      <input value={user[key]} onChange={e => setUser(previous => ({ ...previous, [key]: e.target.value }))} disabled={busy} {...options} />
    </label>
  )
  return <form className={styles.form} onSubmit={save}>
    <label className={styles.field}>{tr(locationLabel)}
      <input value={modalPayload[`${locationKey}_name`] || locationId || ''} readOnly />
    </label>
    <p className={styles.hint}>{tr('Найдите пользователя по ИНН. Если его нет в системе, зарегистрируйте и сразу назначьте оператором этого объекта.')}</p>
    <div className={styles.search}>
      <label className={styles.field}>{tr('ИНН')}
        <input value={inn} onChange={e => changeInn(e.target.value)} inputMode="numeric" maxLength={14} autoComplete="off"
          disabled={busy} aria-describedby="operator-inn-hint" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); search() } }} />
      </label>
      <button type="button" className={management.button} onClick={search} disabled={inn.length !== 14 || checking || busy}>
        {checking ? <LoaderCircle size={16} aria-hidden="true" /> : <Search size={16} aria-hidden="true" />}{tr(checking ? 'Проверяем ИНН в базе…' : 'Найти по ИНН')}
      </button>
    </div>
    <small id="operator-inn-hint" className={styles.hint}>{tr('14 цифр. Водителя нельзя назначить оператором базы.')}</small>
    {error && <div role="alert" className={management.error}>{error}</div>}
    {lookup?.status === 'blocked' && <div role="alert" className={management.error}>{tr(lookup.detail)}</div>}
    {ready && lookup.status === 'found' && <div className={styles.person} role="status">
      <strong>{lookup.user.name}</strong><span>{lookup.user.inn}</span>
      {lookup.user.phone && <span>{lookup.user.phone}</span>}
      <small>{tr('Пользователь найден. Можно назначить оператором этого объекта.')}</small>
      {lookup.previous_locations?.length > 0 && <small>{tr('Оператор будет автоматически снят с прежних объектов и назначен на этот. Перенос сохранится в журнале.')}
        {' '}{lookup.previous_locations.map(location => location.name).join(', ')}
      </small>}
    </div>}
    {isNew && <>
      <div role="status" className={management.success}>{tr('ИНН свободен. Можно зарегистрировать оператора.')}</div>
      <div className={styles.grid}>
        {field('password', 'Первоначальный пароль', { type: 'password', required: true, minLength: 8, autoComplete: 'new-password' })}
        {field('surname', 'Фамилия', { required: true, maxLength: 150, autoComplete: 'family-name' })}
        {field('first_name', 'Имя', { required: true, maxLength: 150, autoComplete: 'given-name' })}
        {field('last_name', 'Отчество', { maxLength: 150, autoComplete: 'additional-name' })}
        {field('number_phone', 'Телефон', { type: 'tel', maxLength: 30, autoComplete: 'tel' })}
        {field('email', 'Электронная почта', { type: 'email', autoComplete: 'email' })}
      </div>
    </>}
    {ready && <>
      <label className={styles.field}>{tr('Документы')}
        <LocalizedFileInput multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" disabled={busy}
          onChange={e => { const selected = Array.from(e.target.files || []); setFiles(previous => [...previous, ...selected]) }} />
      </label>
      {files.length > 0 && <ul className={styles.files}>{files.map((file, index) => <li key={index}>
        <span>{file.name}</span><button type="button" className={management.button} disabled={busy}
          onClick={() => setFiles(previous => previous.filter((_, i) => i !== index))} aria-label={tr('Удалить выбранный файл')}><X size={14} aria-hidden="true" /></button>
      </li>)}</ul>}
    </>}
    <div className={styles.actions}>
      <button type="button" className={management.button} onClick={closeModal} disabled={busy}>{tr('Отмена')}</button>
      <button type="submit" className={management.primary} disabled={!ready || busy || checking}>
        {busy ? <LoaderCircle size={16} aria-hidden="true" /> : <UserPlus size={16} aria-hidden="true" />}
        {tr(busy ? 'Сохранение…' : isNew ? 'Зарегистрировать и назначить' : 'Назначить оператором')}
      </button>
    </div>
  </form>
}
